<template>
  <div class="tw-scope w-full h-screen flex flex-col bg-dark text-white">
    <!-- 顶部导航栏 -->
    <header class="relative z-[100] flex items-center justify-between bg-dark px-4 py-2 shadow-md">
      <div class="text-lg font-bold text-primary">🌉 深圳市桥隧群数字孪生推演平台</div>
      <div class="flex space-x-2">
        <button @click="refreshAllObjectColors" class="btn-primary">刷新颜色</button>
        <button @click="resetAllObjectColors" class="btn-secondary">恢复颜色</button>
        <button @click="resetToTopView" class="btn-secondary">俯视视角</button>
        <button @click="resetToSideView" class="btn-secondary">轴侧视角</button>
        <!-- 图层控制按钮 -->
        <div class="relative">
          <button @click="showLayerMenu = !showLayerMenu" class="btn-secondary">图层控制</button>
          <div v-if="showLayerMenu" class="absolute right-0 mt-2 bg-gray-800 text-white p-2 rounded shadow-lg z-50 space-y-1">
            <div v-for="(visible, layer) in layerVisibility" :key="layer" class="flex items-center space-x-2">
              <input type="checkbox" v-model="layerVisibility[layer]" @change="updateLayerVisibility" />
              <label>{{ layer }}</label>
            </div>
          </div>
        </div>
        <!-- 🏠 返回主页按钮 -->
        <button @click="goHome" class="btn-secondary" title="返回主页">🏠</button>
      </div>
    </header>

    <!-- 时间选择面板（主视图区左上角） -->
    <div class="absolute top-20 left-[280px] z-50">
      <!-- 折叠状态横条 -->
      <div
        @click="showTimePanel = !showTimePanel"
        class="flex items-center justify-between w-44 px-3 py-1.5
               bg-gray-800/60 backdrop-blur-md
               hover:bg-gray-700/80 transition-all duration-300
               rounded-lg border border-blue-400/50
               shadow-md shadow-blue-500/30 cursor-pointer select-none"
      >
        <!-- 左侧图标 + 时间 -->
        <div class="flex items-center space-x-2">
          <!-- SVG时钟图标，直接显示，不依赖FontAwesome -->
          <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div class="flex flex-col leading-tight">
            <span class="text-[10px] text-gray-300">{{ selectedDate }}</span>
            <span class="text-xs text-blue-400 font-semibold tracking-wide">{{ formattedTime }}</span>
          </div>
        </div>

        <!-- 右侧箭头 -->
        <svg
          class="w-3 h-3 text-gray-400 transform transition-transform duration-300"
          :class="showTimePanel ? 'rotate-180' : ''"
          fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- 折叠面板内容 -->
      <transition name="fade-slide">
        <div
          v-if="showTimePanel"
          class="mt-1 w-52 p-3 rounded-lg
                 bg-gray-800/70 backdrop-blur-md
                 border border-blue-400/40
                 shadow-lg shadow-blue-500/30
                 space-y-2"
        >
          <!-- 日期选择器 -->
          <label class="block text-xs text-gray-300 mb-1">选择日期</label>
          <select
            v-model="selectedDate"
            class="w-full px-2 py-1 text-xs rounded-md
                   bg-gray-900/80 border border-gray-600 text-gray-200
                   focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option v-for="day in weekDays" :key="day" :value="day">
              {{ day }}
            </option>
          </select>

          <!-- 时间滑块 -->
          <label class="block text-xs text-gray-300 mt-2">选择时间段</label>
          <input
            type="range"
            min="0"
            max="23"
            step="1"
            v-model="selectedHour"
            @input="onSliderInput"
            class="w-full accent-blue-400"
          />
          <div class="text-center text-xs text-blue-400 font-semibold mt-1">
            {{ formattedTime }}
          </div>
        </div>
      </transition>
    </div>

    <div class="tw-scope flex flex-1 overflow-hidden">
      <!-- 左侧功能栏 -->
      <aside class="w-64 bg-gray-900 p-4 overflow-y-auto">
        <BaseAccordion>
          <BaseAccordionItem title="🚦 级联动态推演">
            <template #content>
              <div class="space-y-2">
                <div class="relative" ref="eventNodeDropdownContainer">
                  <input
                    v-model="eventNode"
                    placeholder="桥隧节点编号"
                    class="input-box"
                    @focus="onEventNodeFocus"
                    @blur="onEventNodeBlur"
                    @input="onEventNodeInput"
                    autocomplete="off"
                  />
                  <ul
                    v-if="eventNodeDropdownVisible && eventNodeFilteredOptions.length > 0"
                    class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg z-[100]"
                  >
                    <li
                      v-for="option in eventNodeFilteredOptions"
                      :key="option.node"
                      @mousedown.prevent="selectEventNodeOption(option)"
                      class="px-3 py-2 cursor-pointer hover:bg-cyan-600 hover:text-white transition-colors text-sm text-gray-200 border-b border-gray-700 last:border-b-0 flex items-center gap-2"
                    >
                      <span class="font-mono text-cyan-400 font-semibold whitespace-nowrap">{{  option.displayNode }}</span>
                      <span class="text-gray-400 truncate">{{ option.name }}</span>
                    </li>
                  </ul>
                </div>
                <select v-model.number="eventType" class="input-box">
                  <option :value="1">检测</option>
                  <option :value="2">维修</option>
                  <option :value="3">突发事件管控</option>
                </select>
                <button @click="runSimulation" class="btn-primary w-full">开始推演</button>
                <button
                  @click="toggleFlowSpritesVisibility"
                  :class="[flowSpritesVisible ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ flowSpritesVisible ? '关闭流量' : '显示流量' }}
                </button>
                <button
                  @click="showSimulationChart = !showSimulationChart"
                  :class="[showSimulationChart ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ showSimulationChart ? '隐藏推演结果' : '显示推演结果' }}
                </button>
              </div>
            </template>
          </BaseAccordionItem>

          <BaseAccordionItem title="🚧 节点流量传播推演">
            <template #content>
              <div class="space-y-2">
                <div class="relative" ref="spreadEventNodeDropdownContainer">
                  <input
                    v-model="spreadEventNode"
                    placeholder="请输入桥隧节点编号"
                    class="input-box"
                    @focus="onSpreadEventNodeFocus"
                    @blur="onSpreadEventNodeBlur"
                    @input="onSpreadEventNodeInput"
                    autocomplete="off"
                  />
                  <ul
                    v-if="spreadEventNodeDropdownVisible && spreadEventNodeFilteredOptions.length > 0"
                    class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg z-[100]"
                  >
                    <li
                      v-for="option in spreadEventNodeFilteredOptions"
                      :key="option.node"
                      @mousedown.prevent="selectSpreadEventNodeOption(option)"
                      class="px-3 py-2 cursor-pointer hover:bg-cyan-600 hover:text-white transition-colors text-sm text-gray-200 border-b border-gray-700 last:border-b-0 flex items-center gap-2"
                    >
                      <span class="font-mono text-cyan-400 font-semibold whitespace-nowrap">{{  option.displayNode }}</span>
                      <span class="text-gray-400 truncate">{{ option.name }}</span>
                    </li>
                  </ul>
                </div>
                <select v-model.number="spreadEventType" class="input-box">
                  <option :value="1">检测</option>
                  <option :value="2">维修</option>
                  <option :value="3">突发事件管控</option>
                </select>

                <!-- ✅ 播放速度控制区域 -->
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-400">播放速度：</span>
                  <div class="flex gap-2">
                    <button @click="spreadSpeed = 0.5" :class="{ 'btn-active': spreadSpeed === 0.5 }" class="btn-mini">0.5x</button>
                    <button @click="spreadSpeed = 1.0" :class="{ 'btn-active': spreadSpeed === 1.0 }" class="btn-mini">1.0x</button>
                    <button @click="spreadSpeed = 3.0" :class="{ 'btn-active': spreadSpeed === 3.0 }" class="btn-mini">3.0x</button>
                  </div>
                </div>

                <button
                  @click="handleSpreadToggle"
                  :class="[isSpreading ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ isSpreading ? '停止传播推演' : '开始传播推演' }}
                </button>

                <button
                  v-if="gifUrl"
                  @click="toggleSpreadVisibility"
                  :class="[showGif ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ showGif ? '隐藏传播动画' : '显示传播动画' }}
                </button>

                <div v-if="showGif" class="pt-2">
                  <img :src="gifUrl" alt="传播动画" class="w-full border border-gray-700 rounded" />
                </div>
              </div>
            </template>
          </BaseAccordionItem>

          <BaseAccordionItem title="📊 桥隧群等级">
            <template #content>
              <div class="flex items-center space-x-1 mb-2">
                <input v-model="searchInput" type="text" class="input-box flex-1" placeholder="🔍 搜索对象名称..." @keyup.enter="performSearch"/>
                <button @click="performSearch" class="btn-secondary px-2 py-1">搜索</button>
              </div>
              <button @click="showFullRankList = !showFullRankList" :class="[showFullRankList ? 'btn-secondary' : 'btn-primary', 'w-full']">
                {{ showFullRankList ? '折叠列表 ▲' : '展开列表 ▼' }}
              </button>
              <div v-show="showFullRankList">
                <div class="max-h-[360px] overflow-y-auto border border-gray-700 rounded-lg shadow-inner bg-gray-900">
                  <table class="w-full text-[12px]">
                    <thead>
                      <tr>
                        <th class="border-b pb-1 text-center w-10 cursor-pointer select-none" @click="sortTableBy('node')">
                          编号<br> <span v-if="tableSortColumn === 'node'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span><span v-else class="text-gray-500">⇅</span>
                        </th>
                        <th class="border-b pb-1 text-center cursor-pointer select-none" @click="sortTableBy('name')">
                          名称<br> <span v-if="tableSortColumn === 'name'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span><span v-else class="text-gray-500">⇅</span>
                        </th>
                        <th class="border-b pb-1 text-center whitespace-nowrap px-2 w-10 cursor-pointer select-none" @click="sortTableBy('congestion')">
                          等级<br> <span v-if="tableSortColumn === 'congestion'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span><span v-else class="text-gray-500">⇅</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-700">
                      <tr v-for="[name, info] in filteredRankMap" :key="info.node" class="hover:bg-gray-800 transition-colors duration-200"
                          @mouseenter="showTooltip(info, $event)" @mouseleave="hideTooltip">
                        <td class="text-center text-[12px] py-1 px-2 font-mono text-gray-300">{{ Number(info.node) + 1  }}</td>
                        <td class="text-left text-[12px] py-1 px-2 font-medium cursor-pointer hover:underline" @click="focusOnObject(name)">
                          <span class="inline-block w-3 h-3 rounded-full mr-1 border" :style="{ backgroundColor: rankColors[info.congestion] }"></span>
                          {{ name }}
                        </td>
                        <td class="text-center text-[12px] py-1 px-2 text-blue-400">{{ info.congestion }}</td>
                      </tr>
                      <div v-if="tooltip.visible" :style="{ top: tooltip.y+'px', left: tooltip.x+'px' }"
                          class="fixed bg-gray-800 text-gray-200 text-xs px-2 py-1 rounded shadow-lg border border-gray-700 z-50 whitespace-nowrap">
                        节点编号: {{ Number(tooltip.node) + 1 }}
                      </div>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </BaseAccordionItem>

          <BaseAccordionItem title="🎨 图例筛选">
            <template #content>
              <div v-for="(color, rank) in rankColors" :key="rank" @click="toggleRankFilter(Number(rank))"
                   class="flex items-center cursor-pointer hover:opacity-80 space-x-2 py-1">
                <span class="w-4 h-4 rounded" :style="{ backgroundColor: color }"></span>
                <span>等级 {{ rank }}</span>
              </div>
              <div @click="toggleRankFilter(null)" class="cursor-pointer mt-1 hover:opacity-80">显示全部</div>
            </template>
          </BaseAccordionItem>
        </BaseAccordion>
      </aside>

      <!-- 主视图区 -->
<main class="relative flex-1">
  <!-- 1. Three.js 渲染容器 (只在 three 模式显示) -->
  <div v-show="viewMode === 'three'" ref="threeCanvas" class="absolute inset-0"></div>

  <!-- 2. 地图 iframe (只在 map 模式显示) -->
<iframe
  ref="mapIframe"
  v-show="viewMode === 'map'"
  :srcdoc="mapHtml"
  class="absolute inset-0 w-full h-full border-0"
  loading="lazy"
  @load="onMapIframeLoad"
></iframe>

  <!-- 3. 模型加载动画 -->
  <div v-if="modelLoading" class="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-40 z-50">
    <div class="loader-ring"></div>
    <p class="text-white mt-4 text-lg font-semibold tracking-wide">模型加载中...</p>
  </div>

  <!-- 4. 右上角视图切换按钮 -->
  <div class="absolute top-4 right-4 z-50 flex gap-2">
    <button
      @click="viewMode = 'three'"
      :class="viewMode === 'three' ? 'bg-cyan-500' : 'bg-gray-700'"
      class="px-3 py-1 rounded text-white hover:bg-gray-600 transition"
    >
      3D模型
    </button>
    <button
      @click="viewMode = 'map'"
      :class="viewMode === 'map' ? 'bg-cyan-500' : 'bg-gray-700'"
      class="px-3 py-1 rounded text-white hover:bg-gray-600 transition"
    >
      网络拓扑
    </button>
  </div>

  <!-- 5. 推演结果图表（独立于视图，始终置顶） -->
  <div
    v-show="showSimulationChart"
    ref="simulateBarChart"
    class="absolute bottom-6 right-6 w-[600px] h-[400px] rounded-lg p-4 shadow-lg z-40"
    style="background-color: #1e1e2f;"
  ></div>

  <!-- 6. 其他仅模型视图才显示的覆盖层（饼图、调试面板） -->
  <div v-show="viewMode === 'three'">
    <!-- 原有的饼图 -->
    <div v-show="showPieChart" ref="pieChartContainer" class="absolute top-6 right-6 w-[600px] h-[400px] rounded-lg p-4 shadow-lg" style="background-color: rgba(192,192,192,1);"></div>
    <!-- 原有的调试面板 -->
    <div ref="debugPanel" v-if="debugObject" class="absolute z-50 bg-black bg-opacity-70 p-4 rounded shadow-lg text-sm space-y-1" :style="{ top: debugPanelPosition.y + 'px', left: debugPanelPosition.x + 'px' }" @mousedown.prevent="onPanelMouseDown">
      <div><strong>名称:</strong> {{ selectedNodeInfo.name }}</div>
      <div><strong>节点编号:</strong> {{ Number(selectedNodeInfo.node) + 1 }}</div>
      <div><strong>当前等级:</strong> {{ selectedNodeInfo.congestion }}</div>
      <div><strong>速度:</strong> {{ selectedNodeInfo.speed }} km/h</div>
      <div><strong>流量:</strong> {{ selectedNodeInfo.flow }} 辆/h</div>
      <button class="btn-secondary w-full mt-2" @click="toggleObjectsByNodeVisibility">
        {{ selectedObject && selectedObject.userData.node && objectVisibilityMap[selectedObject.userData.node] === false ? '显示对象' : '隐藏对象' }}
      </button>
    </div>
  </div>
</main>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import * as echarts from 'echarts';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
//import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
//import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { modelLoader } from '@/utils/ModelLoader.js';//预加载模型
//import LineChart from './LineChart.vue';
import api from '@/utils/api';
import axios from 'axios';
import gsap from 'gsap';
import mapHtml from '@/assets/map_shenzhen.html?raw';

import BaseAccordion from './BaseAccordion.vue';
import BaseAccordionItem from './BaseAccordionItem.vue';
//import { FALSE } from 'node-sass';
import '@/assets/main.css';

export default {
  name: 'ThreeJSComponent',
  components: { BaseAccordion, BaseAccordionItem },
  data() {
    return {
      mapIframeReady: false,
    pendingMapMessages: [], 
    viewMode: 'three',  
    mapHtml: mapHtml, 
    allNodeOptions: [],
      eventNodeDropdownVisible: false,
      eventNodeFilteredOptions: [],
      spreadEventNodeDropdownVisible: false,
      spreadEventNodeFilteredOptions: [],
      flowSpritesVisible: false,
      flowSprites: [],         // 存储所有精灵对象
      allNodesFlowData: [],    // 全部节点流量数据
      modelLoading: true,
      selectedNodeInfo: {
        name: '—',
        node: '—',
        congestion: '—',
        speed: '—',
        flow: '—',
      },
      latestNodeDataMap: new Map(),
      tableSortColumn: null,
      tableSortOrder: 'asc',
      searchInput: '',
      chartData: [
        { product: '检测次数', '2020': 56.5, '2021': 82.1, '2022': 88.7, '2023': 70.1, '2024': 53.4, '2025': 8 },
        { product: '维修次数', '2020': 51.1, '2021': 51.4, '2022': 55.1, '2023': 53.3, '2024': 73.8, '2025': 6 },
        { product: '交通事故', '2020': 40.1, '2021': 62.2, '2022': 69.5, '2023': 36.4, '2024': 45.2, '2025': 3 },
        { product: '应急管控', '2020': 25.2, '2021': 37.1, '2022': 41.2, '2023': 18, '2024': 33.9, '2025': 4 }
      ],
      scene: null,
      camera: null,
      isAnimating: true,
      renderer: null,
      raycaster: new THREE.Raycaster(),
      mouse: new THREE.Vector2(),
      controls: null,
      selectedObject: null,
      hoverItem: null,
      debugObject: null,//控制调试面板
      editRank: null,
      isRankLoading:false,
      showFullRankList:false,
      currentObjectColorHex: '#ffffff',
      tooltip: { visible: false, node: null, x: 0, y: 0 },
      selectedRankFilter: null, // 当前筛选 rank（null 表示显示全部）
      showRankPanel: false,
      globalRankMap: {}, // { name: rank }
      searchKeyword: '',     // 搜索关键字
      eventNode: '198',
      eventType: 1,
      showTimePanel: false,
      debounceTimer: null,
      weekDays: ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期天'],
      selectedDate: (() => {
        const getDayToIndex = [6, 0, 1, 2, 3, 4, 5];
        const weekDays = ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期天'];
        return weekDays[getDayToIndex[new Date().getDay()]];
      })(),
      selectedHour: new Date().getHours(),
      simulationResult: [],
      sortOrder: 'desc', // 默认降序
      isLoadingSimulation: false,
      isDraggingPanel: false,
      dragOffset: { x: 0, y: 0 },
      debugPanelPosition: { x: 100, y: 100 }, // 默认位置
      cameraLastPos: new THREE.Vector3(),
      hideExcluded: false,
      groundOriginalY: -20,
      showSimulationChart: false,//控制模拟结果显示
      showPieChart: false,
      objectVisibilityMap: {},  // key: node 值，value: true/false 表示是否隐藏
      blinkingInterval: null,
      blinkingTimeout: null,
      isSpreading: false, // 控制是否正在传播
      spreadAbortController: null, // 控制取消传播（可选增强）
      spreadEventNode: '',
      spreadEventType: 1,
      spreadSpeed: 1.0, // 默认播放速度为正常速度
      showGif: false,
      spreadResultSequence: [],
      gifUrl: '',
      layerVisibility: {
        桥梁: true,
        隧道: true,
        建筑: true,
        路网: true,
        水系: true,
        用地: true
      },
      showLayerMenu: false,
      layerKeywordMap: {
        桥梁: ['桥', 'Bridge'],
        隧道: ['隧', 'Tunnel'],
        建筑: ['building', '楼','Areasbuildingroof','Object','Sphere011_Material002_0'],
        路网: ['road', 'highway', '路','Wayshighway'],
        水系: ['water', '水','外环内主要水系'],
        用地: ['landuse', '用地','Areasleisure','Areaslanduse','Areasnatural']
      },
      rankColors: {
        4: '#ff0000',
        3: '#ffa500',
        2: '#ffff00',
        1: '#00ff00',
        0: '#1e90ff'
      },
      selectedObjectInfo: '',
      simplifiedObjects: [], // 存储简化后的对象
      excludedNames: ['shenzhen_road_clean','map','外环内主要水系','Areasnatural', 'Areasnatural001','Areasleisure','Areaslanduse','Object_25','Sphere011_Material002_0','Areasbuilding','Object_3001','Areasbuildingroof','Object_2001','Wayshighway','Object_2','Object_8','Object_24','Object_23'],  // 添加要排除交互的mesh名称
    };
  },
  computed: {
    // 显示格式化时间
    formattedTime() {
      const hour = Math.floor(this.selectedHour);
      const minute = this.selectedHour % 1 ? '30' : '00'; // 半小时显示
      return `${hour.toString().padStart(2, '0')}:${minute}`;
    },

    filteredRankMap() {
      const keyword = this.searchKeyword?.toLowerCase() || "";
      let entries = Object.entries(this.globalRankMap || {});
      if (keyword) {
        entries = entries.filter(([name]) => name.toLowerCase().includes(keyword));
      }
      if (this.tableSortColumn) {
        entries.sort((a, b) => {
          let valA, valB;
          if (this.tableSortColumn === 'node') {
            valA = a[1].node; valB = b[1].node;
          } else if (this.tableSortColumn === 'name') {
            valA = a[0]; valB = b[0];
          } else if (this.tableSortColumn === 'congestion') {
            valA = a[1].congestion; valB = b[1].congestion;
          }
          if (typeof valA === 'string') {
            return this.tableSortOrder === 'asc'
              ? valA.localeCompare(valB) : valB.localeCompare(valA);
          } else {
            return this.tableSortOrder === 'asc' ? valA - valB : valB - valA;
          }
        });
      } else {
        // 默认按拥堵等级降序
        entries.sort((a, b) => b[1].congestion - a[1].congestion);
      }
      return entries;
    },

    sortedSimulationResult() {
      const sorted = [...this.simulationResult].sort((a, b) => {
        return this.sortOrder === 'desc'
          ? b['Traffic Flow'] - a['Traffic Flow']
          : a['Traffic Flow'] - b['Traffic Flow'];
      });
      return sorted;
    },
  },
  created() {
    // 缓存节流版检查函数
    this.checkCameraMoveThrottled = this.throttle(this.checkCameraMovement, 300);
  },

  methods: {
    // ---------- 地图 iframe 通信 ----------
onMapIframeLoad() {
  const iframeWin = this.$refs.mapIframe.contentWindow;

  const checkMap = setInterval(() => {
    for (const key in iframeWin) {
      try {
        const obj = iframeWin[key];
        // 检测是否为 Leaflet 地图实例
        if (obj && obj._container && obj._layers) {
          iframeWin.map = obj;
          // 确保 Leaflet 全局对象存在
          if (!iframeWin.L) {
            console.warn('Leaflet 未在 iframe 中找到，但继续尝试');
          }
          clearInterval(checkMap);

          // 初始化圆圈管理数组
          iframeWin._spreadCircles = iframeWin._spreadCircles || [];

          // 移除旧监听，防止重复绑定
          iframeWin.removeEventListener('message', iframeWin._spreadMsgHandler);
          
          iframeWin._spreadMsgHandler = (event) => {
            const { type, nodeId } = event.data || {};
if (type === 'spread-step') {
  const coords = iframeWin.nodeCoords[String(nodeId)];
  if (coords) {
    const baseRadius = 6;        // 原来12的一半
    const peakRadius = 10;      // 脉冲峰值
    const circle = new iframeWin.L.circleMarker(coords, {
      radius: baseRadius,
      fillColor: '#ff0000',
      color: 'transparent',     // 去掉外描边
      weight: 0,
      fillOpacity: 0.6,
      zIndexOffset: 10000
    }).addTo(iframeWin.map);
    iframeWin._spreadCircles.push(circle);

    // 脉冲动画：三次变大使出脉冲效果，然后停在基础半径
    let pulseCount = 0;
    const maxPulses = 3;
    const pulseInterval = 300;   // 每次脉冲时长 (可调整)
const pulseIn = () => {
  if (pulseCount >= maxPulses) {
    circle.setRadius(baseRadius);
    return;
  }
  circle.setRadius(peakRadius);
  setTimeout(() => {
    circle.setRadius(baseRadius);
    pulseCount++;
    setTimeout(pulseIn, pulseInterval / 2);
  }, pulseInterval / 2);
};
pulseIn();
  } else {
    console.warn('❌ 找不到节点坐标:', nodeId);
  }
}
          };
          iframeWin.addEventListener('message', iframeWin._spreadMsgHandler);

          // 标记就绪，发送积压消息
          this.mapIframeReady = true;
          this.pendingMapMessages.forEach(msg => this.sendToMap(msg));
          this.pendingMapMessages = [];

          console.log('✅ 地图实例已注入，传播红圈监听已就绪');
          break;
        }
      } catch (e) {
        // 忽略访问某些属性时可能出现的错误
      }
    }
  }, 200);
},

sendToMap(message) {
  if (this.mapIframeReady && this.$refs.mapIframe) {
    this.$refs.mapIframe.contentWindow.postMessage(message, '*');
  } else {
    // 缓存消息，等就绪后发送
    this.pendingMapMessages.push(message);
  }
},
    // ✅ 新增：从 globalRankMap 构建所有节点选项（去重）
buildAllNodeOptions() {
  const map = this.globalRankMap || {};
  const seenNodes = new Set();
  const options = [];
  for (const [name, info] of Object.entries(map)) {
    const node = info.node;
    if (node != null && !seenNodes.has(String(node))) {
      seenNodes.add(String(node));
      options.push({
        node: String(node),              // 原始编号（内部使用）
        displayNode: String(Number(node) + 1),  // 显示编号（前端展示）
        name: name
      });
    }
  }
  // 按编号数值排序
  options.sort((a, b) => {
    const numA = parseInt(a.node, 10);
    const numB = parseInt(b.node, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.node.localeCompare(b.node);
  });
  this.allNodeOptions = options;
},

// ✅ 新增：从输入值提取数字前缀用于过滤
extractNumericPrefix(value) {
  const match = String(value || '').match(/^(\d+)/);
  return match ? match[1] : '';
},

// ✅ 新增：从输入值提取纯节点编号（用于提交给后端）
extractNodeId(value) {
  const match = String(value || '').match(/^(\d+)/);
  if (match) {
    const displayNum = parseInt(match[1], 10);
    // 减 1 得到原始编号，并确保不为负数
    const originalNum = Math.max(displayNum - 1, 0);
    return String(originalNum);
  }
  return String(value);  // 非数字开头则原样返回（后面逻辑可能报错，保持兼容）
},

// ✅ 新增：根据输入前缀过滤选项
smartFilter(value) {
  const input = String(value || '').trim();
  if (!input) return [];
  
  const startsWithDigit = /^\d/.test(input);
  
  if (startsWithDigit) {
    const prefix = (input.match(/^(\d+)/) || [])[1] || input;
    // 只匹配显示编号的前缀
    return this.allNodeOptions.filter(opt =>
      String(opt.displayNode).startsWith(prefix)
    );
  } else {
    const keyword = input.toLowerCase();
    return this.allNodeOptions.filter(opt =>
      opt.name.toLowerCase().includes(keyword)
    );
  }
},

// ===== 级联推演输入框事件处理 =====
onEventNodeFocus() {
  this.eventNodeFilteredOptions = this.smartFilter(this.eventNode);
  this.eventNodeDropdownVisible = this.eventNodeFilteredOptions.length > 0;
},

onEventNodeBlur() {
  setTimeout(() => {
    this.eventNodeDropdownVisible = false;
  }, 150);
},

onEventNodeInput() {
  this.eventNodeFilteredOptions = this.smartFilter(this.eventNode);
  this.eventNodeDropdownVisible = this.eventNodeFilteredOptions.length > 0;
},

selectEventNodeOption(option) {
  this.eventNode = `${option.displayNode} ${option.name}`;
  this.eventNodeDropdownVisible = false;
},

// ===== 传播推演输入框事件处理 =====
onSpreadEventNodeFocus() {
  this.spreadEventNodeFilteredOptions = this.smartFilter(this.spreadEventNode);
  this.spreadEventNodeDropdownVisible = this.spreadEventNodeFilteredOptions.length > 0;
},

onSpreadEventNodeBlur() {
  setTimeout(() => {
    this.spreadEventNodeDropdownVisible = false;
  }, 150);
},

onSpreadEventNodeInput() {
  this.spreadEventNodeFilteredOptions = this.smartFilter(this.spreadEventNode);
  this.spreadEventNodeDropdownVisible = this.spreadEventNodeFilteredOptions.length > 0;
},

selectSpreadEventNodeOption(option) {
  this.spreadEventNode = `${option.displayNode} ${option.name}`;
  this.spreadEventNodeDropdownVisible = false;
},

// ✅ 新增：点击页面其他地方关闭下拉
onGlobalClick(e) {
  const eventContainer = this.$refs.eventNodeDropdownContainer;
  if (eventContainer && !eventContainer.contains(e.target)) {
    this.eventNodeDropdownVisible = false;
  }
  const spreadContainer = this.$refs.spreadEventNodeDropdownContainer;
  if (spreadContainer && !spreadContainer.contains(e.target)) {
    this.spreadEventNodeDropdownVisible = false;
  }
},
    // ========== 数字精灵相关方法 ==========

    toggleFlowSpritesVisibility() {
      if (!this.flowSpritesVisible) {
        this.addNumbersToAllNodes().then(() => {
          this.flowSpritesVisible = true;
        });
      } else {
        this.clearAllFlowSprites();
        this.flowSpritesVisible = false;
      }
    },

    async fetchAllNodesFlow() {
      try {
        // ⚠️ 接口路径请根据实际后端修改，如果不存在则精灵功能无法使用
        const response = await axios.get('/api/szflow/all-nodes-flow');
        if (response.data.success) {
          this.allNodesFlowData = response.data.data;
          return this.allNodesFlowData;
        } else {
          console.warn('获取全节点流量失败:', response.data.message);
          return [];
        }
      } catch (err) {
        console.error('获取全节点流量失败:', err);
        return [];
      }
    },

    getColorByFlow(flowValue) {
      if (flowValue < 1000) return { hex: 0x003CF6, css: '#003CF6' };
      else if (flowValue < 2000) return { hex: 0x002BB8, css: '#002BB8' };
      else return { hex: 0x001A7A, css: '#001A7A' };
    },

    createPillar(flowValue) {
      const minHeight = 150, maxHeight = 1500;
      const maxFlow = Math.max(...this.allNodesFlowData.map(d => d.flow), 1);
      const pillarHeight = minHeight + (flowValue / maxFlow) * (maxHeight - minHeight);
      const pillarRadius = 20;
      const geometry = new THREE.CylinderGeometry(pillarRadius, pillarRadius, pillarHeight, 16);
      const material = new THREE.MeshBasicMaterial({
        color: this.getColorByFlow(flowValue).hex,
        transparent: true,
        opacity: 0.9
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.y = pillarHeight / 2;
      return { pillarMesh: mesh, pillarHeight };
    },

    createNumberSpriteWithPillar(position, flowData) {
      const flowValue = flowData.flow;
      const flowText = flowData.formattedFlow || flowValue.toFixed(0);
      const colorInfo = this.getColorByFlow(flowValue);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const res = 4;
      canvas.width = 1536 * res / 3;
      canvas.height = 384 * res / 3;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `bold ${360 * res / 3}px Arial`;
      ctx.fillStyle = colorInfo.css;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 0;
      ctx.strokeText(flowText, canvas.width / 2, canvas.height / 2);
      ctx.fillText(flowText, canvas.width / 2, canvas.height / 2);

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 1.0 });
      const sprite = new THREE.Sprite(spriteMaterial);
      const spriteWidth = 300, spriteHeight = 75;
      sprite.scale.set(spriteWidth, spriteHeight, 1);

      const { pillarMesh, pillarHeight } = this.createPillar(flowValue);
      const group = new THREE.Group();
      group.add(pillarMesh);
      group.add(sprite);
      sprite.position.y = pillarHeight + 15;
      group.position.copy(position);
      group.position.y += 5; // 略高于模型

      return {
        group, sprite, pillar: pillarMesh,
        node: flowData.node, name: flowData.name,
        flow: flowText, flowValue, flowColor: colorInfo
      };
    },

    async addNumbersToAllNodes() {
      const flowData = await this.fetchAllNodesFlow();
      if (flowData.length === 0) {
        console.warn('没有流量数据，无法创建数字精灵');
        return;
      }
      this.clearAllFlowSprites();
      let addedCount = 0;
      const EPSILON = 0.01;

      for (const nodeData of flowData) {
        let targetObject = null;
        let targetPosition = null;
        this.scene.traverse(child => {
          if (child.isMesh && child.name && child.name.includes(nodeData.name)) {
            targetObject = child;
            const pos = new THREE.Vector3();
            child.getWorldPosition(pos);
            targetPosition = pos;
          }
        });
        if (targetObject && targetPosition) {
          // 过滤原点附近的节点（未绑定模型）
          if (Math.abs(targetPosition.x) < EPSILON && Math.abs(targetPosition.z) < EPSILON) {
            console.log(`跳过原点节点 ${nodeData.name}`);
            continue;
          }
          const flowSprite = this.createNumberSpriteWithPillar(targetPosition, nodeData);
          this.scene.add(flowSprite.group);
          this.flowSprites.push(flowSprite);
          addedCount++;
        }
      }
      console.log(`🎯 为 ${addedCount} 个节点添加了流量精灵`);
    },

    clearAllFlowSprites() {
      this.flowSprites.forEach(item => {
        if (item.group) {
          this.scene.remove(item.group);
          if (item.sprite && item.sprite.material) item.sprite.material.dispose();
          if (item.pillar) {
            if (item.pillar.geometry) item.pillar.geometry.dispose();
            if (item.pillar.material) item.pillar.material.dispose();
          }
        }
      });
      this.flowSprites = [];
    },

    updateSpritesScreenSize() {
      if (!this.camera || !this.renderer || !this.flowSprites.length) return;
      const fov = this.camera.fov * Math.PI / 180;
      const height = this.renderer.domElement.clientHeight;
      if (height === 0) return;
      const tanHalfFov = Math.tan(fov / 2);
      const targetPixels = 10;
      const aspectRatio = 600 / 150;
      const cameraPos = this.camera.position;
      const worldPos = new THREE.Vector3();
      this.flowSprites.forEach(item => {
        if (!item.sprite) return;
        item.sprite.getWorldPosition(worldPos);
        const distance = worldPos.distanceTo(cameraPos);
        const targetHeightWorld = targetPixels * (2 * distance * tanHalfFov) / height;
        const targetWidthWorld = targetHeightWorld * aspectRatio;
        item.sprite.scale.set(targetWidthWorld, targetHeightWorld, 1);
      });
    },

    sortTableBy(column) {
      if (this.tableSortColumn === column) {
        this.tableSortOrder = this.tableSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.tableSortColumn = column;
        this.tableSortOrder = 'asc';
      }
    },

    performSearch() {
      this.searchKeyword = this.searchInput;
      this.showFullRankList = true;
    },

    handleMiddleClick(event) {
      if (event.button !== 1) return;
      event.preventDefault();
      const canvas = this.$refs.threeCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.camera);
      const planeY0 = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const intersection = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(planeY0, intersection)) {
        const offset = this.camera.position.clone().sub(this.controls.target);
        gsap.to(this.controls.target, {
          x: intersection.x, y: intersection.y, z: intersection.z,
          duration: 1.0, ease: 'power2.out',
          onUpdate: () => {
            this.camera.position.copy(this.controls.target).add(offset);
            this.controls.update();
          }
        });
      }
    },

    updateLatestNodeDataMap(latestData) {
      if (!Array.isArray(latestData)) {
        this.latestNodeDataMap = new Map();
        return;
      }
      this.latestNodeDataMap = new Map(
        latestData.map(item => [Number(item.node), item])
      );
    },

    updateDebugPanelBySelectedObject(obj = this.selectedObject) {
      if (!obj) {
        this.selectedNodeInfo = { name: '—', node: '—', congestion: '—', speed: '—', flow: '—' };
        return;
      }
      const node = Number(obj.userData?.node);
      if (!Number.isFinite(node)) {
        this.selectedNodeInfo = {
          name: obj.name || '—', node: '—',
          congestion: obj.userData?.congestion ?? '—',
          speed: obj.userData?.speed ?? '—',
          flow: obj.userData?.flow ?? '—',
        };
        return;
      }
      const latestItem = this.latestNodeDataMap.get(node);
      this.selectedNodeInfo = {
        name: obj.name || '—',
        node: node,
        congestion: latestItem?.congestion ?? obj.userData?.congestion ?? '—',
        speed: latestItem?.speed ?? obj.userData?.speed ?? '—',
        flow: latestItem?.flow ?? obj.userData?.flow ?? '—',
      };
    },

    toggleSpreadVisibility() {
      this.showGif = !this.showGif;

      // 控制 Three.js 中对象可见性
      const visible = this.showGif;
      this._activeSprites?.forEach(sprite => sprite.visible = visible);
      this._activeRings?.forEach(ring => ring.visible = visible);
      this._activeSpheres?.forEach(sphere => sphere.visible = visible); // 若你也有 sphere 动画对象
    },

    showTooltip(info, evt) {
      this.tooltip = {
        visible: true,
        node: info.node,
        x: evt.clientX + 10, // 鼠标右下角
        y: evt.clientY + 10,
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },


    onSliderInput() {

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.runSimulation(); // 停止滑动0.5秒后触发刷新
      }, 500);
    },

    findObjectByNodeId(nodeId) {
      let result = null;
      this.scene.traverse(obj => {
        if (obj.userData?.node === Number(nodeId)) {
          result = obj;
        }
      });
      return result;
    },

async playSpreadAnimation(spreadSequence) {
  this.clearSpreadGraphics();
  if (!spreadSequence || spreadSequence.length === 0) {
    console.warn('⚠️ 传播序列为空，跳过播放');
    return;
  }

  this._shouldStopSpread = false;
  this.sendToMap({ type: 'spread-clear' });
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  this._activeSprites = new Map();
  this._activeRings = new Map();
  this._activeSpheres = new Map();

  this.resetToTopView();

  // ✅ 启动 sprite 动画（仅一次）
  if (!this._spriteAnimationStarted) {
    this._spriteAnimationActive = true;
    const animateSprites = () => {
      requestAnimationFrame(animateSprites);
      if (!this._spriteAnimationActive) return;
      const time = performance.now() * 0.005;
      this._activeSprites.forEach((sprite, nodeId) => {
        if (sprite.userData.done) return;
        const base = 80;
        const amplitude = 20;
        const s = base + amplitude * Math.sin(time + nodeId);
        const isMax = s > 98;
        if (isMax && !sprite.userData.lastWasMax) {
          sprite.userData.pulseCount = (sprite.userData.pulseCount || 0) + 1;
          sprite.userData.lastWasMax = true;
        } else if (!isMax) {
          sprite.userData.lastWasMax = false;
        }
        if (sprite.userData.pulseCount >= 3) {
          sprite.scale.set(100, 100, 1);
          sprite.material.opacity = 1.0;
          sprite.userData.done = true;
        } else {
          sprite.scale.set(s, s, 1);
          sprite.material.opacity = 0.6 + 0.4 * Math.sin(time + nodeId);
        }
      });
    };
    animateSprites();
  }

  // ✅ 改为直接遍历原始序列，不再预过滤
  for (const step of spreadSequence) {
    if (this._shouldStopSpread) {
      console.log('🛑 用户中断传播流程');
      break;
    }

    const { node } = step;

    // ✅ 无论有没有三维模型，都先通知拓扑图
    this.sendToMap({ 
      type: 'spread-step', 
      nodeId: String(node)
    });

    // 尝试获取三维模型对象
    const object = this.findObjectByNodeId(node);
    if (!object) {
      console.warn(`⚠️ 未找到 node=${node} 的模型对象，仅拓扑显示`);
      await delay(400 / this.spreadSpeed);
      continue;
    }

    // 获取世界坐标并检查有效性
    const worldPos = new THREE.Vector3();
    object.getWorldPosition(worldPos);

    if (worldPos.length() < 1e-2) {
      console.warn(`⚠️ node=${node} 位置≈原点，仅拓扑显示`);
      await delay(400 / this.spreadSpeed);
      continue;
    }
    if (worldPos.y > 50) {
      console.warn(`⚠️ node=${node} 高度 y=${worldPos.y.toFixed(2)} > 50，仅拓扑显示`);
      await delay(400 / this.spreadSpeed);
      continue;
    }

    // 以下是原有三维动画代码（环形扩散圈、脉冲球体等），完全保留
    // ----- 创建 Ring 扩散圈 -----
    const ringGeometry = new THREE.RingGeometry(5, 6, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(worldPos);
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 998;
    this.scene.add(ring);
    this._activeRings.set(node, ring);

    let t = 0;
    const ringInterval = setInterval(() => {
      t += 0.04;
      const scale = 10 + t * 60;
      ring.scale.set(scale, scale, 1);
      ring.material.opacity = 0.5 - t * 0.5;
      if (t >= 1.0) {
        this.scene.remove(ring);
        this._activeRings.delete(node);
        clearInterval(ringInterval);
      }
    }, 33);

    // ✅ 创建并添加球体 sprite
    if (!this._activeSprites.has(node)) {
      const geometry = new THREE.SphereGeometry(1, 16, 16);
      const material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.scale.set(1, 0.1, 1);
      sphere.rotation.x = -Math.PI / 2;
      sphere.position.copy(worldPos);
      sphere.renderOrder = 999;
      sphere.userData.pulseCount = 0;
      sphere.userData.lastWasMax = false;
      this.scene.add(sphere);
      this._activeSprites.set(node, sphere);
      console.log(`✅ 添加球体 node=${node}，position=`, sphere.position.toArray());
    }

    const stepDelay = 400 / this.spreadSpeed;
    await delay(stepDelay);
  }
},

async handleSpreadToggle() {
  if (this.isSpreading) {
    this._shouldStopSpread = true;
    this.spreadAbortFlag = true;
    this.isSpreading = false;
    this.clearSpreadGraphics();
    return;
  }

  // ✅ 提取纯数字编号
  const pureNodeId = this.extractNodeId(this.spreadEventNode);
  if (!pureNodeId || !this.spreadEventType) {
    alert('请输入节点编号和事件类型');
    return;
  }

  this.showGif = false;
  this.spreadResultSequence = [];
  this.gifUrl = '';
  this.spreadAbortFlag = false;
  this.isSpreading = true;

  try {
    const res = await axios.post('https://bridgetunnelgroup.site/api/shenzhen/traffic/spread', {
      node: pureNodeId,  // ✅ 使用提取后的纯编号
      type: this.spreadEventType,
    });

        if (res.data.success) {
          this.spreadResultSequence = res.data.data.spread_sequence;
          this.gifUrl = 'https://bridgetunnelgroup.site/' + res.data.data.gif_path.replace(/^\//, '');

          // ✅ 播放 Three.js 动画（支持中断）
          this.playSpreadAnimation(this.spreadResultSequence);

          // ✅ 轮询 GIF 生成
          let retryCount = 0;
          const maxRetries = 10;
          const delay = (ms) => new Promise((r) => setTimeout(r, ms));
          while (retryCount < maxRetries) {
            try {
              await axios.head(this.gifUrl);
              this.showGif = true;
              console.log('🎬 GIF 动画已生成！');
              return;
            } catch {
              retryCount++;
              console.log(`第 ${retryCount} 次检查 GIF...`);
              await delay(5000);
            }
          }

          alert('GIF 动画生成超时，请稍后手动刷新查看');
        } else {
          alert('模拟失败: ' + res.data.message);
        }
      } catch (err) {
        console.error('请求出错:', err);
        alert('网络错误或后端异常');
      }
    },

    clearSpreadGraphics() {
      // 1. 停止动画循环（防止新帧修改 Map）
      this._spriteAnimationActive = false;

      // 2. 清除所有球体（sprites）
      if (this._activeSprites) {
        this._activeSprites.forEach(s => this.scene.remove(s));
        this._activeSprites.clear();
        this._activeSprites = null;
      }

      // 3. 清除所有扩散圈（rings）
      if (this._activeRings) {
        this._activeRings.forEach(r => this.scene.remove(r));
        this._activeRings.clear();
        this._activeRings = null;
      }

      // 4. 重置标志
      this._spriteAnimationStarted = false;
      this._shouldStopSpread = false;
      this.sendToMap({ type: 'spread-clear' });
    },

    makeObjectsBlinkByNode(nodeId) {
      if (!nodeId || !this.scene) return;

      // 清除之前的闪烁
      if (this.blinkingInterval) clearInterval(this.blinkingInterval);
      if (this.blinkingTimeout) clearTimeout(this.blinkingTimeout);

      const targetMeshes = [];

      // 收集所有 node 相同的 mesh
      this.scene.traverse(child => {
        if (child.isMesh && child.userData.node === nodeId) {
          const material = Array.isArray(child.material) ? child.material[0] : child.material;
          targetMeshes.push({
            mesh: child,
            material: material,
            originalColor: material.color.clone()
          });
        }
      });

      if (targetMeshes.length === 0) return;

      let visible = true;

      // 启动闪烁
      this.blinkingInterval = setInterval(() => {
        visible = !visible;
        targetMeshes.forEach(({ material, originalColor }) => {
          material.color.set(visible ? 0xff0000 : originalColor);
        });
      }, 300);

      // 5 秒后停止
      this.blinkingTimeout = setTimeout(() => {
        clearInterval(this.blinkingInterval);
        targetMeshes.forEach(({ material, originalColor }) => {
          material.color.set(originalColor);
        });
        this.blinkingInterval = null;
        this.blinkingTimeout = null;
      }, 5000);
    },

    toggleObjectsByNodeVisibility() {
      if (!this.selectedObject || !this.selectedObject.userData.node) return;

      const targetNode = this.selectedObject.userData.node;
      const isCurrentlyVisible = !(this.objectVisibilityMap[targetNode] ?? true); // 默认是 true（可见）

      // 遍历场景中所有对象，设置相同 node 的对象 visible
      this.scene.traverse(obj => {
        if (obj.isMesh && obj.userData.node === targetNode) {
          obj.visible = isCurrentlyVisible;
        }
      });

      // 更新可见性记录
      this.$set(this.objectVisibilityMap, targetNode, isCurrentlyVisible);
    },

    goHome() {
          this.resetAllObjectColors();
      // 若你使用 Vue Router：
      this.$router.push('/roadnet');
      // 若你没有用 Vue Router，而是静态页面跳转：
      // window.location.href = '/';
    },

    checkCameraMovement() {
      if (this.debugObject && this.camera.position.distanceTo(this.cameraLastPos) > 50) {
        this.debugObject = null;
      }
      this.cameraLastPos.copy(this.camera.position);
    },

    // 简单节流函数（限制执行频率）
    throttle(func, limit = 100) {
      let lastCall = 0;
      return (...args) => {
        const now = new Date().getTime();
        if (typeof func === 'function' && now - lastCall >= limit) {
          lastCall = now;
          func.apply(this, args);
        }
      };
    },

    // 简单防抖函数（延迟执行，避免频繁触发）
    debounce(func, delay = 200) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        if (typeof func === 'function') {
          timer = setTimeout(() => {
            func.apply(this, args);
          }, delay);
        }
      };
    },

    getIntersectedObject(event, maxDistance = Infinity) {
      const rect = this.$refs.threeCanvas.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.camera);

      let closestObj = null;
      let closestDistance = Infinity;

      for (const obj of this.simplifiedObjects) {
        if (!obj.visible) continue;
        if (this.excludedNames.includes(obj.name)) continue;

        const box = obj.userData.boundingBox?.clone(); // clone 以便膨胀
        if (!box || box.isEmpty()) continue;

        // 根据距离动态膨胀点击范围（越远越容易选中）
        const distanceToCam = this.camera.position.distanceTo(obj.position);
        const expandFactor = Math.min(1.0, distanceToCam / 200);
        box.expandByScalar(0.2 * expandFactor);

        const intersectionPoint = new THREE.Vector3();
        if (this.raycaster.ray.intersectBox(box, intersectionPoint)) {
          const distance = this.camera.position.distanceTo(intersectionPoint);
          if (distance < closestDistance && distance < maxDistance) {
            closestDistance = distance;
            closestObj = obj;
          }
        }
      }

      return closestObj;
    },

    onPanelMouseDown(e) {
      this.isDraggingPanel = true;
      this.dragOffset = {
        x: e.clientX - this.debugPanelPosition.x,
        y: e.clientY - this.debugPanelPosition.y,
      };
      document.addEventListener('mousemove', this.onPanelMouseMove);
      document.addEventListener('mouseup', this.onPanelMouseUp);
    },

    onPanelMouseMove(e) {
      if (this.isDraggingPanel) {
        this.debugPanelPosition = {
          x: e.clientX - this.dragOffset.x,
          y: e.clientY - this.dragOffset.y,
        };
      }
    },

    onPanelMouseUp() {
      this.isDraggingPanel = false;
      document.removeEventListener('mousemove', this.onPanelMouseMove);
      document.removeEventListener('mouseup', this.onPanelMouseUp);
    },

    updateLayerVisibility() {
      this.simplifiedObjects.forEach(obj => {
        for (const [layer, keywords] of Object.entries(this.layerKeywordMap)) {
          if (keywords.some(keyword => obj.name.includes(keyword))) {
            obj.visible = this.layerVisibility[layer];
            break;
          }
        }
      });
        const mapObj = this.simplifiedObjects.find(obj => obj.name === 'map');
        if (mapObj) {
          mapObj.visible = this.layerVisibility['水系'];
        }
    },

    resetToTopView() {
      this.camera.position.set(2502, 28708, -251);
      //this.camera.lookAt(new THREE.Vector3(2502, 108, -251));
      if (this.controls) {
        this.controls.target.set(2502, 108, -251);
        this.controls.update();
      }
    },

    resetToSideView() {
      this.camera.position.set(-8227.422914720086, 1562.4670575337568, 8315.08813266791);
      //this.camera.lookAt(new THREE.Vector3(182, 188, 157));
      if (this.controls) {
        this.controls.target.set(-7023.166601678367, 36.45408925469403, 4662.094432275458);
        this.controls.update();
      }
    },

    toggleExcludedVisibility() {
      this.hideExcluded = !this.hideExcluded;

      this.simplifiedObjects.forEach(obj => {
        if (this.excludedNames.includes(obj.name)) {
          obj.visible = !this.hideExcluded;
        }
      });

      // 找到地面对象并移动
      const ground = this.scene.getObjectByName('ground');
      if (ground) {
        const newY = this.hideExcluded
          ? this.groundOriginalY - 20  // 向下移动 20
          : this.groundOriginalY;     // 复位到原始位置

        // 使用 gsap 动画平滑移动
        gsap.to(ground.position, {
          y: newY,
          duration: 0.6,
          ease: 'power2.out',
        });
      }
    },

    focusOnObject(name) {
      const targetObj = this.simplifiedObjects.find(obj => obj.name && obj.name.includes(name));
      if (!targetObj) {
        console.warn('❗未找到对应对象:', name);
        return;
      }

      const box = new THREE.Box3().setFromObject(targetObj);
      const center = new THREE.Vector3();
      box.getCenter(center);

      const offset = new THREE.Vector3(0, 100, 200); // 视角偏移位置
      const newPos = center.clone().add(offset);

      const selectedNode = targetObj?.userData?.node
      this.makeObjectsBlinkByNode(selectedNode);    // 开始闪烁

      // 动画飞过去
      gsap.to(this.camera.position, {
        duration: 1.2,
        x: newPos.x,
        y: newPos.y,
        z: newPos.z,
        ease: 'power2.out',
        onUpdate: () => {
          this.camera.lookAt(center);
        }
      });

      // 控制器的 target 也更新
      gsap.to(this.controls.target, {
        duration: 1.2,
        x: center.x,
        y: center.y,
        z: center.z,
        ease: 'power2.out',
        onUpdate: () => {
          this.controls.update();
        }
      });
    },

    async loadGlobalRanks() {
      try {
        const data = await this.fetchImportanceData();
        const map = {};
        data.forEach(item => {
          map[item.name] = {
            node: item.node,            // 节点编号
            congestion: item.congestion // rank
          };
        });
        this.globalRankMap = map; // { name: { node, congestion } }
        this.buildAllNodeOptions();
        console.log('✅ 全局 rankMap 已加载（包含 node）:', this.globalRankMap);
      } catch (err) {
        console.error('加载全局 rank 失败:', err);
      }
    },

    /** ========== 运行模拟核心方法 ========== */
async runSimulation() {
  // ✅ 提取纯数字编号（用户可能输入了 "9 深圳湾大桥"）
  const pureNodeId = this.extractNodeId(this.eventNode);
  if (!pureNodeId || !this.eventType) {
    alert("请输入完整信息（事件节点、类型）！");
    return;
  }

  // 计算星期几（1~7）
  const weekdayIndex = this.weekDays.indexOf(this.selectedDate);
  if (weekdayIndex === -1) {
    alert('日期选择异常');
    return;
  }
  const targetWeekday = weekdayIndex + 1;
  const targetHour = parseInt(this.selectedHour);

  try {
    await axios.post('https://bridgetunnelgroup.site/api/shenzhen/traffic-v2/run', {
      targetWeekday,
      targetHour,
      eventNode: pureNodeId,
      eventType: this.eventType,
    });

    const latestData = await this.fetchImportanceData();
    this.simulationResult = latestData;

    this.showSimulationChart = true;

    await this.loadGlobalRanks(); // 内部已调用 buildAllNodeOptions

    this.$nextTick(() => {
      setTimeout(() => {
        const chartDom = this.$refs.simulateBarChart;
        if (chartDom) {
          this.renderSimulationChart();
        } else {
          console.error("模拟图表容器未找到");
        }
      }, 50);
    });

    this.refreshAllObjectColors();
    console.log('模拟完成，结果已更新:', this.simulationResult);
  } catch (err) {
    console.error('模拟失败:', err);
    alert('模拟失败，请检查输入或控制台错误信息');
  }
},

    // 渲染图表（按 congestion 排序或流量排序均可，这里还是按流量前20展示）
    renderSimulationChart() {
      const chartDom = this.$refs.simulateBarChart;
      if (!chartDom || chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
        console.warn('图表容器未就绪，延迟重试...');
        setTimeout(() => this.renderSimulationChart(), 100);
        return;
      }

      // 销毁旧实例避免重复初始化
      if (echarts.getInstanceByDom(chartDom)) {
        echarts.dispose(chartDom);
      }
      const myChart = echarts.init(chartDom);

      // ✅ 使用全部模拟结果，不截取
      const allData = [...this.simulationResult]
        .filter(item => item.flow > 1e-6);

      const option = {
        backgroundColor: '#1e1e2f', //  图表背景色
        title: {
          text: '桥隧群流量分布',
          left: 'center',
          textStyle: { color: '#ffffff' } // ✅ 标题颜色
        },
        tooltip: { trigger: 'item' },
        grid: {
          left: 10,
          right: 10,
          top: 50,
          bottom: 30, // 增加底部间距，避免被滚动条和文字遮挡
          containLabel: true
        },
        dataZoom: [
          {
            type: 'slider',  // 底部滑动条
            show: true,
            xAxisIndex: 0,
            start: 0,        // 默认展示前 20%
            end: 20          // 可调节初始展示比例
          },
          {
            type: 'inside',  // 鼠标滚轮或触摸板也可滑动
            xAxisIndex: 0
          }
        ],
        xAxis: {
          type: 'category',
          data: allData.map(i => i.name || `节点${Number(i.node) + 1}`),
          axisLabel: { color: '#dddddd', rotate: 45, margin: 10 },
          axisLine: { lineStyle: { color: '#888888' } },
          axisTick: { lineStyle: { color: '#888888' } }
        },
        yAxis: { type: 'value',
          name: '流量 (veh/h)',
          nameTextStyle: { color: '#dddddd' },
          axisLabel: { color: '#dddddd' },
          axisLine: { lineStyle: { color: '#888888' } },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } } // 网格线浅灰
        },
        series: [{
          type: 'bar',
          data: allData.map(i => ({
            value: i.flow,
            itemStyle: { color: `#${this.getColorByRank(i.congestion).toString(16).padStart(6, '0')}` } // 直接使用后端的congestion
          }))
        }]
      };

      myChart.setOption(option);
    },

    // 获取 rank 颜色（直接使用 congestion）
    getRankColor(rank) {
      return this.rankColors[rank] || '#ffffff';
    },

    // 刷新颜色逻辑：直接根据 congestion 赋色
    async refreshAllObjectColors() {
      try {
        // 获取合并后的节点数据（包含 node、name、congestion）
        const data = await this.fetchImportanceData();
        if (!Array.isArray(data)) {
          console.error('❌ 获取的重要性数据不是数组:', data);
          return;
        }

        // 遍历场景中的简化模型对象
        this.simplifiedObjects.forEach(obj => {
          // 模糊匹配对象名
          const match = data.find(item => obj.name.includes(item.name));
          if (match) {
            const congestion = match.congestion;
            obj.material.color.setHex(this.getColorByRank(congestion));
          }
        });

        console.log('✅ 模型颜色已刷新');
      } catch (err) {
        console.error('❌ 刷新颜色失败:', err);
      }
    },

    resetAllObjectColors() {
      this.simplifiedObjects.forEach(obj => {
        if (obj && obj.material && obj.userData.originalColor) {
          obj.material.color.copy(obj.userData.originalColor);
        }
      });
      console.log('✅ 所有模型颜色已重置为默认色');
    },

    async submitAllRanks() {
      try {
        const updates = Object.entries(this.globalRankMap).map(([name, rank]) => ({ name, rank }));
        await api.post('/importance/ranking/bulk', updates);
        alert('批量提交成功');
        this.refreshAllObjectColors(); // 刷新模型颜色
      } catch (err) {
        console.error('批量提交失败', err);
        alert('提交失败');
      }
    },

    toggleRankFilter(rank) {
      this.selectedRankFilter = (this.selectedRankFilter === rank) ? null : rank;

      // 遍历所有对象，根据 rank 决定是否可见
      this.simplifiedObjects.forEach(obj => {
        const rankOfObj = this.getRankFromColor(obj.material.color.getHex());
        obj.visible = (this.selectedRankFilter === null || rankOfObj === this.selectedRankFilter);
      });
    },

    getRankFromColor(hexValue) {
      for (const [rank, hex] of Object.entries(this.rankColors)) {
        const colorHex = new THREE.Color(hex).getHex(); // 转为数字 hex
        if (hexValue === colorHex) {
          return Number(rank);
        }
      }
      return null;
    },

    logSelectedObject() {
      console.log('当前选中对象完整结构:', this.selectedObject);
    },

    updateCurrentColorPreview() {
      if (!this.selectedObject || !this.selectedObject.material) {
        this.currentObjectColorHex = '#cccccc';
        return;
      }

      const color = this.selectedObject.material.color;
      this.currentObjectColorHex = `#${color.getHexString()}`;
    },

    async fetchCurrentRank(name) {
      this.isRankLoading = true;
      try {
        // 调用新接口获取最新模拟结果
        const { data } = await api.get('/shenzhen/traffic-v2/latest');

        // 按名称精确匹配
        const item = data.find(i => i.name === name);

        if (item && typeof item.congestion === 'number') {
          // 使用 congestion 作为 rank
          this.editRank = item.congestion;
        } else {
          this.editRank = null;
        }
      } catch (err) {
        console.error('获取 rank 失败:', err);
        this.editRank = null;
      } finally {
        this.isRankLoading = false;
      }
    },

    async updateRank() {
      if (!this.selectedObject || !this.editRank) return;

      const name = this.selectedObject.name;
      const rank = this.editRank;

      try {
        await api.post('/importance/ranking', { name, rank });

        // 更新颜色
        this.selectedObject.material.color.set(this.rankColors[rank] || '#cccccc');

        alert('等级已保存');
      } catch (err) {
        console.error('更新失败:', err);
        alert('更新失败，请检查控制台');
      }
    },

    async fetchImportanceData() {
      try {
        //并行查询两个接口
        const [mappingRes, congestionRes] = await Promise.all([
          api.get('shenzhen/node-importance/ranking/full'),
          api.get('shenzhen/traffic-v2/latest')
        ]);
        const mappingData = mappingRes.data || []       // [{ node, name, rank(废弃) }]
        const congestionData = congestionRes.data || [] // [{ node, congestion,speed,flow }]

        // 合并数据
        const mergedData = congestionData.map(congItem => {
          const mapItem = mappingData.find(m => m.node === congItem.node);
          return {
            node: congItem.node,
            name: mapItem ? mapItem.name : `Node-${congItem.node}`,
            congestion: congItem.congestion,
            speed: congItem.speed,
            flow: congItem.flow,
          };
        });
    // ✅ 新增：更新节点数据缓存
    this.updateLatestNodeDataMap(mergedData);
    // ✅ 新增：如果调试面板（右键标签）已打开，立即刷新
    if (this.selectedObject) {
      this.updateDebugPanelBySelectedObject(this.selectedObject);
    }
        console.log('✅ 合并后的节点数据:', mergedData);
        return mergedData;
      } catch (err) {
        console.error('获取节点排序信息失败：', err);
        return [];
      }
    },

    getColorByRank(congestion) {
      const colorMap = {
        4: 0xff0000, // 红色
        3: 0xffa500, // 橙色
        2: 0xffff00, // 黄色
        1: 0x00ff00, // 绿色
        0: 0x1e90ff, // 蓝色
      };
      return colorMap[congestion] || 0xcccccc; // 默认灰色
    },

    addGround() {
      // 白色地面（尽量大）
      const groundGeometry = new THREE.PlaneGeometry(1000000, 1000000); //  非无限但足够大
      const groundMaterial = new THREE.MeshStandardMaterial({
        //color: 0xE4EBF2,
        //color: 0xFFFFFF,//淡白色
        //color: 0xe6ecf2,//淡灰蓝色
        //color: 0xf5f5f5,//淡米白色
        color: 0xCCD6E0,//暗灰蓝
        roughness: 0.95,
        metalness: 0.1,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });

      const ground = new THREE.Mesh(groundGeometry, groundMaterial);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -30; // 稍微降低地面
      ground.receiveShadow = true;
      ground.userData.ignoreRaycast = true;
      ground.renderOrder = -1;
      ground.name = 'ground';
      this.scene.add(ground);

      // 网格辅助线
      //const grid = new THREE.GridHelper(100000, 1000, 0xe0e0e0, 0xe0e0e0);
      //grid.position.y = -19.9; // 比地面略高，防止遮挡
      //this.scene.add(grid);
    },

    addSkybox() {
      const loader = new THREE.TextureLoader();
      const texture = loader.load('textures/sky.png'); // 替换为实际的等距矩形贴图路径
      texture.mapping = THREE.EquirectangularReflectionMapping; // 设置为等距矩形贴图映射

      // ✅ 使用场景背景设置，而不是创建球体
      this.scene.background = texture;

      // ✅ 可选：同时设置环境贴图用于反射
      this.scene.environment = texture;
    },

    printModelStructure(object, depth = 0) {
      const indent = '  '.repeat(depth);
      console.log(`${indent}${object.name || '(Unnamed)'} - ${object.type}`);
      if (object.children) {
        object.children.forEach((child) => this.printModelStructure(child, depth + 1));
      }
    },

    updateInfo() {
      if (this.selectedObject) {
        console.log('Updated Object Info:', this.selectedObject.userData);
        alert(`Object Info Updated:
        ID: ${this.selectedObject.userData.id}
        Type: ${this.selectedObject.userData.type}
        Strain: ${this.selectedObject.userData.strain}`);
      } else {
        alert('No object selected to update.');
      }
    },

    initChart() {
      const chartDom = this.$refs.pieChartContainer;
      const myChart = echarts.init(chartDom);

      const option = {
        backgroundColor: 'rgba(192,192,192,0.6)',
        aria: {
            enabled: true,
            decal: {
                show: true
            }
        },
        legend: {},
        tooltip: {
          trigger: 'axis',
          showContent: false
        },
        dataset: {
          source: [
            ['product', '2020', '2021', '2022', '2023', '2024', '2025'],
            ...this.chartData.map(item => [item.product, item['2020'], item['2021'], item['2022'], item['2023'], item['2024'], item['2025']])
          ]
        },
        xAxis: { type: 'category' },
        yAxis: { gridIndex: 0 },
        grid: { top: '55%' },
        series: [
          { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
          { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
          { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
          { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
          {
            type: 'pie',
            id: 'pie',
            radius: '30%',
            center: ['50%', '25%'],
            emphasis: { focus: 'self' },
            label: { formatter: '{b}: {@2012} ({d}%)' },
            encode: { itemName: 'product', value: '2012', tooltip: '2012' }
          }
        ]
      };

      myChart.setOption(option);
      // 更新饼图内容，当鼠标移动到折线图上时触发
      myChart.on('updateAxisPointer', (event) => {
        const xAxisInfo = event.axesInfo[0];
        if (xAxisInfo) {
          const dimension = xAxisInfo.value + 1;  // 获取当前的维度（年份）
          myChart.setOption({
            series: {
              id: 'pie',
              label: {
                formatter: `{b}: {@[${dimension}]} ({d}%)`
              },
              encode: {
                value: dimension,
                tooltip: dimension
              }
            }
          });
        }
      });
    },

    initThree() {
      // 初始化场景
      this.scene = new THREE.Scene();
      //this.scene.background = new THREE.Color("#ECFAFF");
      this.scene.background = new THREE.Color("#B0D8FF"); // 介于天蓝和白之间

      // 设置渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.setClearColor(0xf4f4f4);//柔和白色背景
      this.$refs.threeCanvas.appendChild(this.renderer.domElement);

      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.3;
      // 临时禁用阴影以判断是否是阴影导致昏暗
      this.renderer.shadowMap.enabled = false;

      // 初始化相机
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.camera = new THREE.PerspectiveCamera(30, width / height, 1, 10000000);
      this.camera.position.set(-8227.422914720086, 1562.4670575337568, 8315.08813266791);
      this.camera.lookAt(0, 0, 0);

      // 初始化轨道控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.set(-7023.166601678367, 36.45408925469403, 4662.094432275458);
      this.controls.update();
      this.controls.enableDamping = false;

      //this.controls.addEventListener('change', () => {
      //console.log('camera.position =', this.camera.position.toArray());
      //console.log('controls.target =', this.controls.target.toArray());
      //});

      // 添加光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
      this.scene.add(ambientLight);

      // 半球光：增强阴影面光照
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x999999, 1.2);
      hemiLight.position.set(0, 500, 0);
      this.scene.add(hemiLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
      directionalLight.position.set(0, 2000, 3000);
      this.scene.add(directionalLight);

      const backLight = new THREE.DirectionalLight(0xffffff, 3); // 可调节强度
      backLight.position.set(0, -2000, -3000); // 与主光源位置相反
      this.scene.add(backLight);
      // 加载模型
      this.loadModel();
      //this.addSkybox();
      this.addGround();

      // 添加事件监听
      this.$refs.threeCanvas.addEventListener('click', this.onMouseClick);

      // 开始渲染循环
      this.animate();
    },

    async loadModel() {
      console.log('Loading model via global loader...');
      this.modelLoading = true;                 // 开始加载
      try {
        await modelLoader.loadModel('SZ9', '/models/SZ9.glb');
        const model = modelLoader.getModelClone('SZ9');
        model.scale.set(0.3, 0.3, 0.3);
        model.updateMatrixWorld(true);

        const importanceMap = await this.fetchImportanceData();
        this.updateLatestNodeDataMap(importanceMap);

        model.traverse(child => {
          if (child.isMesh) {
            if (!child.name && child.parent && child.parent.name) child.name = child.parent.name;
            child.userData.boundingBox = new THREE.Box3().setFromObject(child).clone();
            child.castShadow = true;
            child.receiveShadow = true;
            const match = importanceMap.find(item => item.name && child.name.includes(item.name));
            if (match && typeof match.node === 'number') {
              if (!child.userData.originalColor) child.userData.originalColor = child.material.color.clone();
              Object.assign(child.userData, {
                node: match.node, rank: match.congestion,
                congestion: match.congestion, speed: match.speed, flow: match.flow,
              });
            }
            this.simplifiedObjects.push(child);
          }
        });

        this.scene.add(model);
        this.updateLayerVisibility();
        console.log('Model loaded successfully.');
      } catch (error) {
        console.error('Error loading model:', error);
      } finally {
        this.modelLoading = false;              // 加载完成
      }
    },

animate() {
  if (!this.isAnimating) return;

  // ✅ 核心改动：只有当前模式是 'three' 时才执行 Three.js 的逻辑
  if (this.viewMode === 'three') {
    this.updateSpritesScreenSize();

    // 限制目标点不低于地面，防止视角钻地
    const groundY = 10;
    const targetY = this.controls.target.y;
    const distance = this.camera.position.distanceTo(this.controls.target);
    const ratio = (groundY - targetY) / distance;
    const clampedRatio = Math.max(-1, Math.min(1, ratio));
    this.controls.maxPolarAngle = Math.acos(clampedRatio);
    if (this.controls.target.y < groundY) {
      this.controls.target.y = groundY;
    }

    this.renderer.render(this.scene, this.camera);
    this.checkCameraMoveThrottled();
    this.cameraLastPos.copy(this.camera.position);
    this.controls.update();

    if (this.skybox) this.skybox.position.copy(this.camera.position);
  }

  // ✅ 无论什么模式，这行一定要放在 if 外面，保证动画循环不断
  requestAnimationFrame(this.animate);
},

    onMouseClick(event) {
      this.debugObject = null;
      const closestObj = this.getIntersectedObject(event);
      if (closestObj && closestObj !== this.selectedObject) {
        this.highlightObject(closestObj);
      }
    },

    highlightObject(object) {
      if (!object || !object.material) return;

      // 恢复上一个选中对象颜色
      if (this.selectedObject && this.selectedObject !== object) {
        this.resetHighlight(this.selectedObject);
      }

      //  应用高亮颜色
      this.applyHighlight(object);

      // 更新当前选中对象引用
      this.selectedObject = object;
    },

    resetHighlight(object) {
      if (!object || !object.material) return;

      if (object.material.emissive && object.currentHex !== undefined) {
        object.material.emissive.setHex(object.currentHex);
      } else if (object.material.color && object.currentColor !== undefined) {
        object.material.color.setHex(object.currentColor);
      }
    },

    applyHighlight(object) {
      if (!object || !object.material) return;

      if (object.material.emissive) {
        object.currentHex = object.material.emissive.getHex();
        object.material.emissive.setHex(0xff0000);
      } else if (object.material.color) {
        object.currentColor = object.material.color.getHex();
        object.material.color.setHex(0xff0000);
      }
    },

    onRightClick(event) {
      event.preventDefault();
      const closestObj = this.getIntersectedObject(event, Infinity);
      if (closestObj) {
        if (closestObj !== this.selectedObject) {
          this.highlightObject(closestObj);
        } else {
          this.selectedObject = closestObj;
        }
        this.debugObject = closestObj;
        this.updateDebugPanelBySelectedObject(closestObj);   // 使用实时数据
        this.currentObjectColorHex = '#' + closestObj.material.color.getHexString();
        this.editRank = closestObj.userData?.rank ?? null;
        this.debugPanelPosition = { x: event.clientX, y: event.clientY };
      }
    },

    resetModel() {
      if (this.selectedObject) {
        const material = this.selectedObject.material;

        if (material) {
          // 如果有 emissive 属性，恢复高亮前的颜色
          if ('emissive' in material && material.emissive) {
            material.emissive.setHex(this.selectedObject.currentHex || 0x000000);
          }
          // 否则恢复原始颜色
          else if ('color' in material && material.color) {
            material.color.setHex(this.selectedObject.currentColor || 0xffffff);
          }
        }

        this.selectedObject = null;
        this.selectedObjectInfo = '';
      }
    },
  },
  watch: {
    showRankPanel(val) {
      if (val) this.loadGlobalRanks();
    }
  },
  async mounted() {
    // 按 P 键打印相机位置和目标点
    this._onGlobalClick = this.onGlobalClick.bind(this);
    document.addEventListener('click', this._onGlobalClick);
    this._onKeyDown = (e) => {
      if (e.key === 'p' || e.key === 'P') {
        console.log('camera.position =', this.camera.position.toArray());
        console.log('controls.target =', this.controls.target.toArray());
      }
    };
    window.addEventListener('keydown', this._onKeyDown);
    this.initThree();
    this.initChart();
    this.resizeRendererDebounced = this.debounce(this.resizeRenderer, 300);
    window.addEventListener('resize', this.resizeRendererDebounced);
    //this.refreshAllObjectColors(); // 首次加载
    this.loadGlobalRanks();
    this.cameraLastPos.copy(this.camera.position);
    this.animate(); // 启动渲染循环
    //setInterval(this.refreshAllObjectColors, 600000); // 每 60 秒刷新一次
    this.renderer.domElement.addEventListener('click', this.onMouseClick); // 左键：高亮
    this.renderer.domElement.addEventListener('contextmenu', this.onRightClick); // 右键：调试面板
    this.renderer.domElement.addEventListener('auxclick', this.handleMiddleClick);
    this.renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault()); // 阻止默认菜单
  },
  beforeDestroy() {
      if (this._onGlobalClick) {
    document.removeEventListener('click', this._onGlobalClick);
    this._onGlobalClick = null;
    }
    window.removeEventListener('resize', this.resizeRendererDebounced);
    // 1. 停止动画循环
    this.isAnimating = false;

    // 2. 释放 Three.js 资源
    this.scene.traverse(child => {
      if (child.isMesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });

    // 3. 释放 renderer
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss?.(); // 有些浏览器支持
      this.renderer.domElement = null;
    }

    // 4. 清空对象引用
    this.simplifiedObjects = [];
    this.debugObject = null;
    this.selectedObject = null;
    this.scene = null;
    this.camera = null;
    this.controls?.dispose();
    this.controls = null;

    // 5. 移除事件监听器
    const canvas = this.$refs.threeCanvas;
    if (canvas) {
      canvas.removeEventListener('click', this.onMouseClick);
      canvas.removeEventListener('contextmenu', this.onRightClick);
    }
    window.removeEventListener('resize', this.onWindowResize);

    console.log('🔻 已释放所有资源并销毁组件');
    // 停止监测定时器
    if (this.monitorTimer) clearInterval(this.monitorTimer);
    if (this.deflectionTimer) clearInterval(this.deflectionTimer);
    // 释放监测图表实例
    if (this.monitorChartInstance) this.monitorChartInstance.dispose();
    if (this.deflectionChartInstance) this.deflectionChartInstance.dispose();
    // 移除中键监听
    if (this.renderer?.domElement) {
      this.renderer.domElement.removeEventListener('auxclick', this.handleMiddleClick);
    }
    this.clearAllFlowSprites();
    // 移除键盘监听
    if (this._onKeyDown) {
      window.removeEventListener('keydown', this._onKeyDown);
      this._onKeyDown = null;
    }
  }
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #1f2937;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.loader-ring {
  border: 6px solid rgba(255, 255, 255, 0.2);
  border-top: 6px solid #00d4ff;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.btn-mini {
  @apply text-xs px-2 py-1 border rounded bg-gray-800 hover:bg-gray-600;
}
.btn-active {
  @apply bg-indigo-600 text-white font-bold;
}
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
.btn-primary {
  @apply bg-cyan-500 text-white rounded px-3 py-1 font-bold hover:bg-cyan-400;
}
.btn-secondary {
  @apply bg-gray-700 text-white rounded px-3 py-1 hover:bg-gray-600;
}
.input-box {
  @apply w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1;
}
table th, table td {
  @apply border-b border-gray-600 py-1;
}
.btn-mini {
  @apply text-xs px-2 py-1 border rounded bg-gray-800 hover:bg-gray-600;
}
.btn-active {
  @apply bg-indigo-600 text-white font-bold;
}
</style>